import React, { useState, useEffect } from "react";
import {
  X,
  Trash2,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { FileDeleteJob } from "../../graphql/subscriptions/onFileDeleteJob/types";
import styles from "./MacStyleDeleteIndicator.module.css";

interface MacStyleDeleteIndicatorProps {
  deleteJob: FileDeleteJob | null;
  onClose: () => void;
  hasUploadIndicator?: boolean;
}

const MacStyleDeleteIndicator: React.FC<MacStyleDeleteIndicatorProps> = ({
  deleteJob,
  onClose,
  hasUploadIndicator = false,
}) => {
  const [showFailedItems, setShowFailedItems] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Auto-hide after successful completion
  useEffect(() => {
    if (deleteJob?.status === "completed" && !hasFailedItems()) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000); // Auto-hide after 3 seconds if successful
      return () => clearTimeout(timer);
    }
  }, [deleteJob, onClose]);

  if (!deleteJob) return null;

  const hasFailedItems = () => {
    return (
      deleteJob.failed_items && Object.keys(deleteJob.failed_items).length > 0
    );
  };

  const getStatusIcon = () => {
    switch (deleteJob.status) {
      case "pending":
        return (
          <Loader2 className={`${styles.icon} ${styles.spinning}`} size={14} />
        );
      case "processing":
        return (
          <Loader2 className={`${styles.icon} ${styles.spinning}`} size={14} />
        );
      case "completed":
        if (hasFailedItems()) {
          return (
            <AlertCircle
              className={`${styles.icon} ${styles.warning}`}
              size={14}
            />
          );
        }
        return (
          <CheckCircle
            className={`${styles.icon} ${styles.success}`}
            size={14}
          />
        );
      default:
        return <Trash2 className={styles.icon} size={14} />;
    }
  };

  const getStatusText = () => {
    switch (deleteJob.status) {
      case "pending":
        return "Preparing to delete...";
      case "processing":
        return "Deleting files...";
      case "completed":
        if (hasFailedItems()) {
          const failedCount = Object.keys(deleteJob.failed_items!).length;
          return `Deletion completed with ${failedCount} error${failedCount > 1 ? "s" : ""}`;
        }
        return "Deletion complete";
      default:
        return "Delete in progress";
    }
  };

  const progressPercent = parseFloat(deleteJob.progress_percent);
  const isComplete = deleteJob.status === "completed";
  const failedCount = deleteJob.failed_items
    ? Object.keys(deleteJob.failed_items).length
    : 0;

  if (isMinimized) {
    return (
      <div
        className={`${styles.container} ${styles.minimized} ${hasUploadIndicator ? styles.withUpload : ""}`}
      >
        <button
          className={styles.minimizedButton}
          onClick={() => setIsMinimized(false)}
          type="button"
        >
          <Trash2 size={14} />
          {isComplete ? (
            <span>
              {hasFailedItems() ? `${failedCount} failed` : "Complete"}
            </span>
          ) : (
            <span>{Math.round(progressPercent)}%</span>
          )}
        </button>
      </div>
    );
  }

  return (
    <div
      className={`${styles.container} ${hasUploadIndicator ? styles.withUpload : ""}`}
    >
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <div className={styles.iconContainer}>{getStatusIcon()}</div>
          <h4 className={styles.title}>{getStatusText()}</h4>
        </div>
        <div className={styles.headerActions}>
          <button
            aria-label="Minimize"
            className={styles.minimizeButton}
            onClick={() => setIsMinimized(true)}
            type="button"
          >
            <ChevronDown size={12} />
          </button>
          {isComplete && (
            <button
              aria-label="Close"
              className={styles.closeButton}
              onClick={onClose}
              type="button"
            >
              <X className={styles.closeIcon} size={12} />
            </button>
          )}
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.statusLine}>
          <span className={styles.status}>
            {deleteJob.successful_deletes} of {deleteJob.total_files}{" "}
            {deleteJob.total_files === 1 ? "item" : "items"} deleted
            {hasFailedItems() && (
              <span className={styles.errorText}> • {failedCount} failed</span>
            )}
          </span>
          <span className={styles.percentage}>
            {Math.round(progressPercent)}%
          </span>
        </div>

        <div className={styles.progressBarContainer}>
          <div
            className={`${styles.progressBar} ${
              isComplete
                ? hasFailedItems()
                  ? styles.progressWarning
                  : styles.progressComplete
                : ""
            }`}
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {hasFailedItems() && (
          <div className={styles.failedSection}>
            <button
              className={styles.toggleFailedButton}
              onClick={() => setShowFailedItems(!showFailedItems)}
              type="button"
            >
              {showFailedItems ? (
                <ChevronDown className={styles.chevron} size={12} />
              ) : (
                <ChevronRight className={styles.chevron} size={12} />
              )}
              Failed items ({failedCount})
            </button>

            {showFailedItems && (
              <div className={styles.failedItemsList}>
                {Object.entries(deleteJob.failed_items!).map(
                  ([uuid, reason]) => (
                    <div className={styles.failedItem} key={uuid}>
                      <XCircle className={styles.failedIcon} size={12} />
                      <div className={styles.failedDetails}>
                        <div className={styles.failedUuid}>
                          {uuid.substring(0, 8)}...
                        </div>
                        <div className={styles.failedReason}>{reason}</div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
        )}

        <div className={styles.stats}>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Total:</span>
            <span className={styles.statValue}>{deleteJob.total_files}</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Processed:</span>
            <span className={styles.statValue}>
              {deleteJob.processed_files}
            </span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Deleted:</span>
            <span className={styles.statValue}>
              {deleteJob.successful_deletes}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MacStyleDeleteIndicator;
